import React, { useState, useEffect } from "react";
import { Modal, Button, Badge, Dropdown, Col } from "react-bootstrap";
import {
  IconCalendar,
  IconClock,
  IconEdit,
  IconTrash,
  IconX,
  IconDots,
  IconFileText,
  IconShare,
  IconStethoscope,
  IconCurrencyReal,
  IconCircleFilled,
  IconSelect,
  IconUserQuestion,
  IconReceipt,
  IconEye,
  IconCheck,
} from "@tabler/icons-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import "./eventDetailsModal.css";
import { formatCurrency, formatDescriptionHybrid } from "./utils";
import NumberFormat from "react-number-format";
import ReceiptViewerModal from "./ReceiptViewerModal";
import { api } from "../../../api/Middleware";
import { CALENDAR_EVENTS_URL } from "../../../api/Urls";
import Swal from "sweetalert2";

// Status de confirmação dos eventos
const CONFIRMATION_STATUS = {
  pending: { label: "Pendente", color: "#ff9900" },
  canceled: { label: "Cancelado", color: "#dc3545" },
  confirmed: { label: "Confirmado", color: "#28a745" },
};

// Status do sinal de pagamento. NULL = não aplicável (não renderiza badge).
const PAYMENT_STATUS = {
  paid: { label: "Sinal pago", color: "#28a745" },
  manual_paid: { label: "Sinal pago (manual)", color: "#0d6efd" },
  rejected: { label: "Comprovante rejeitado", color: "#fd7e14" },
};

const EventDetailsModal = ({
  show,
  onHide,
  event,
  onEdit,
  onDelete,
  canUpdate = false,
  canDelete = false,
  calendars = [],
  appointmentTypes = [],
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [paymentStatusLocal, setPaymentStatusLocal] = useState(null);
  const [markingPayment, setMarkingPayment] = useState(false);

  // Reset do state local sempre que o evento muda (modal pode reusar instância
  // entre eventos diferentes — sem este reset, a marcação manual de um
  // evento "vaza" para o próximo evento aberto na mesma sessão do modal).
  const _currentEventId = event?.id || event?.extendedProps?.id || null;
  useEffect(() => {
    setPaymentStatusLocal(null);
  }, [_currentEventId]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  if (!event) return null;

  const eventStart = new Date(event.start);
  const eventEnd = new Date(event.end);
  const calendarInfo = calendars.find(
    (cal) => cal.id === event.extendedProps?.calendar
  );
  const appointmentType = event.extendedProps?.appointment_type;
  const appointmentTypeInfo = appointmentTypes.find(
    (type) => type.id === appointmentType?.id
  );

  // Status de confirmação

  const confirmationStatus = event.extendedProps?.confirmation || "pending";
  const statusInfo =
    CONFIRMATION_STATUS[confirmationStatus] || CONFIRMATION_STATUS.pending;

  // Sinal de pagamento — null/undefined quando o agendamento não usa esse fluxo.
  // O state local permite refletir imediatamente a marcação manual sem
  // depender do refetch do calendário.
  const paymentStatus = paymentStatusLocal !== null
    ? paymentStatusLocal
    : (event.extendedProps?.payment_status || null);
  const paymentInfo = paymentStatus ? PAYMENT_STATUS[paymentStatus] : null;
  const paymentAmount = event.extendedProps?.payment_amount || null;
  const hasReceipt = Boolean(event.extendedProps?.has_payment_receipt);
  const eventId = event.id || event.extendedProps?.id;
  const formatEventDate = (startDate, endDate) => {
    const isSameDay =
      format(startDate, "yyyy-MM-dd") === format(endDate, "yyyy-MM-dd");

    if (isSameDay) {
      return {
        date: format(startDate, "EEEE, dd 'de' MMMM 'de' yyyy", {
          locale: ptBR,
        }),
        time: `${format(startDate, "HH:mm")} - ${format(endDate, "HH:mm")}`,
      };
    } else {
      return {
        date: `${format(startDate, "dd/MM/yyyy")} - ${format(
          endDate,
          "dd/MM/yyyy"
        )}`,
        time: `${format(startDate, "HH:mm")} - ${format(endDate, "HH:mm")}`,
      };
    }
  };

  const { date, time } = formatEventDate(eventStart, eventEnd);

  const handleMarkPaymentManual = async () => {
    if (!eventId || markingPayment) return;
    const confirm = await Swal.fire({
      title: "Marcar como pago manualmente?",
      text: "Use esta opção quando o cliente pagou presencialmente ou por outro meio, sem comprovante digital.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sim, marcar como pago",
      cancelButtonText: "Cancelar",
    });
    if (!confirm.isConfirmed) return;
    setMarkingPayment(true);
    try {
      const resp = await api.patch(
        `${CALENDAR_EVENTS_URL}${eventId}/mark-payment/`,
        { payment_status: "manual_paid" },
      );
      setPaymentStatusLocal(resp.data?.payment_status || "manual_paid");
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: "Marcado como pago",
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: "Erro ao marcar",
        text: e?.response?.data?.error || "Tente novamente.",
      });
    } finally {
      setMarkingPayment(false);
    }
  };

  const handleEdit = () => {
    setShowDropdown(false);
    onEdit();
  };

  const handleDelete = () => {
    setShowDropdown(false);
    onDelete();
  };


  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        centered
        className={`event-details-modal ${isMobile ? "mobile" : "desktop"}`}
        size={isMobile ? undefined : "md"}
      >
        {/* Header customizado */}
        <div className="event-modal-header">
          <div className="event-header-content">
            <div
              className="event-color-indicator"
              style={{ backgroundColor: "var(--primary)" }}
            />
            <div className="event-header-text">
              <h4 className="event-title">{event.title}</h4>
              <div className="event-header-badges">
                {calendarInfo && (
                  <div className="event-calendar-info">
                    <IconCalendar size={14} />
                    <span>{calendarInfo.name}</span>
                    {calendarInfo.is_shared && (
                      <Badge bg="primary" className="ms-1">
                        <IconShare size={12} className="me-1" />
                        Compartilhado
                      </Badge>
                    )}
                  </div>
                )}

                {/* Badge de status de confirmação */}
                <span
                  style={{
                    backgroundColor: statusInfo.color,
                    color: "#fff",
                  }}
                  className="badge ms-1"
                >
                  <IconCircleFilled size={10} className="me-1" />
                  {statusInfo.label}
                </span>

                {/* Badge de sinal de pagamento — só renderiza quando aplicável */}
                {paymentInfo && (
                  <span
                    style={{
                      backgroundColor: paymentInfo.color,
                      color: "#fff",
                    }}
                    className="badge ms-1"
                    title={
                      paymentAmount
                        ? `Valor: R$ ${Number(paymentAmount).toFixed(2)}`
                        : undefined
                    }
                  >
                    <IconReceipt size={10} className="me-1" />
                    {paymentInfo.label}
                    {paymentAmount ? ` · R$ ${Number(paymentAmount).toFixed(2)}` : ""}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="event-header-actions">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={onHide}
              className="close-btn"
            >
              <IconX size={16} />
            </Button>
          </div>
        </div>

        <Modal.Body className="event-modal-body">
          {/* Informações de data e hora */}
          <div className="event-info-section">
            <div className="event-info-item">
              <div className="event-info-icon">
                <IconCalendar size={20} color={"var(--primary)"} />
              </div>
              <div className="event-info-content">
                <div className="event-info-label">Data</div>
                <div className="event-info-value">{date}</div>
              </div>
            </div>

            <div className="event-info-item">
              <div className="event-info-icon">
                <IconClock size={20} color={"var(--primary)"} />
              </div>
              <div className="event-info-content">
                <div className="event-info-label">Horário</div>
                <div className="event-info-value">{time}</div>
              </div>
            </div>
          </div>

          {/* Informações do tipo de atendimento */}
          {appointmentTypeInfo && (
            <div className="event-info-section">
              <div className="event-info-item">
                <div className="event-info-icon">
                  <IconSelect size={20} color={"var(--primary)"} />
                </div>
                <div className="event-info-content">
                  <div className="event-info-label">Tipo de Atendimento</div>
                  <div className="event-info-value">
                    <div className="appointment-type-info">
                      <span className="appointment-type-name">
                        {appointmentTypeInfo.name}
                      </span>
                      {appointmentTypeInfo.duration && (
                        <Badge bg="light" text="secondary" className="ms-2">
                          {appointmentTypeInfo.duration} min
                        </Badge>
                      )}
                    </div>
                    {appointmentTypeInfo.description && (
                      <div className="appointment-type-description text-muted mt-1">
                        {appointmentTypeInfo.description}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Informações de preço */}
          {event.extendedProps?.price && (
            <div className="event-info-section">
              <div className="event-info-item">
                <div className="event-info-icon">
                  <IconCurrencyReal size={20} color={"var(--primary)"} />
                </div>
                <div className="event-info-content">
                  <div className="event-info-label">Preço</div>
                  <div className="event-info-value">
                    {
                      <NumberFormat
                        displayType={"text"}
                        value={event.extendedProps.price}
                        fixedDecimalScale={true}
                        decimalScale={2}
                        thousandSeparator="."
                        decimalSeparator=","
                        prefix="R$"
                      />
                    }
                  </div>
                </div>
              </div>
            </div>
          )}
          {event.extendedProps?.created_by && (
            <div className="event-info-section">
              <div className="event-info-item">
                <div className="event-info-icon">
                  <IconUserQuestion size={20} color={"var(--primary)"} />
                </div>
                <div className="event-info-content">
                  <div className="event-info-label">Responsável</div>
                  <div className="event-info-value">
                    {event.extendedProps?.created_by}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Sinal de pagamento — renderiza quando há status OU recibo OU
              quando o status é null mas o usuário ainda pode marcar manualmente.
              Só não aparece quando o agendamento nunca usou esse fluxo
              (status null e sem recibo). */}
          {(paymentStatus || hasReceipt) && (
            <div className="event-info-section">
              <div className="event-info-item">
                <div className="event-info-icon">
                  <IconReceipt size={20} color={"var(--primary)"} />
                </div>
                <div className="event-info-content">
                  <div className="event-info-label">Sinal de pagamento</div>
                  <div className="event-info-value">
                    {paymentInfo ? paymentInfo.label : ""}
                    {paymentAmount ? (
                      <span className="text-muted ms-2">
                        (R$ {Number(paymentAmount).toFixed(2)})
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-2" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {hasReceipt && (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => setShowReceipt(true)}
                      >
                        <IconEye size={14} className="me-1" />
                        Ver comprovante
                      </Button>
                    )}
                    {paymentStatus !== "paid" && paymentStatus !== "manual_paid" && canUpdate && (
                      <Button
                        variant="outline-success"
                        size="sm"
                        onClick={handleMarkPaymentManual}
                        disabled={markingPayment}
                      >
                        <IconCheck size={14} className="me-1" />
                        {markingPayment ? "Marcando..." : "Marcar como pago manualmente"}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Descrição */}
          {event.extendedProps?.description && (
            <div className="event-description-section">
              <div className="event-description-header">
                <IconFileText size={18} color={"var(--primary)"} />
                <span>Descrição</span>
              </div>
              <div className="event-description-content">
                {formatDescriptionHybrid(event.extendedProps.description)}
              </div>
            </div>
          )}

          {/* Ações para mobile na parte inferior */}
          {isMobile && (canUpdate || canDelete) && (
            <div className="mobile-actions">
              {canUpdate && (
              <Button
                variant="outline-primary"
                className="mobile-action-btn"
                onClick={handleEdit}
              >
                <IconEdit size={18} className="me-2" />
                Editar Agendamento
              </Button>
              )}
              {canDelete && (
              <Button
                variant="outline-danger"
                className="mobile-action-btn"
                onClick={handleDelete}
              >
                <IconTrash size={18} className="me-2" />
                Cancelar Agendamento
              </Button>
              )}
            </div>
          )}
          {/* Desktop - Botões separados */}
          {!isMobile && (canUpdate || canDelete) && (
            <div className="text-right">
              <hr></hr>
              {canUpdate && (
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleEdit}
                className="me-2"
              >
                <IconEdit size={16} /> Editar
              </Button>
              )}
              {canDelete && (
              <Button
                variant="outline-danger"
                size="sm"
                onClick={handleDelete}
                className="me-2"
              >
                <IconTrash size={16} /> Cancelar
              </Button>
              )}
            </div>
          )}
        </Modal.Body>

        {/* Footer apenas para desktop */}
        {!isMobile && (
          <Modal.Footer className="event-modal-footer">
            <Button variant="secondary" onClick={onHide}>
              Fechar
            </Button>
          </Modal.Footer>
        )}
      </Modal>

      {/* Modal de visualização de comprovante (iframe autenticado) */}
      <ReceiptViewerModal
        show={showReceipt}
        onHide={() => setShowReceipt(false)}
        eventId={eventId}
      />
    </>
  );
};

export default EventDetailsModal;
